'use client'

import { useEffect, useState } from 'react'
import NewsSkeleton from '@/components/NewsSkeleton'
import MastheadLayoutSkeleton from '@/components/skeletons/MastheadLayoutSkeleton'
import { getApiBaseUrl } from '@/lib/config'
import {
  MASTHEAD_LAYOUT,
  parseMastheadLayout,
  type MastheadLayoutId,
} from '@/lib/category-masthead-layout'
import { peekHomeMastheadLayout } from '@/lib/masthead-layout-cache'
import { SITE_CONTAINER, SITE_MAIN_PAD_Y, SITE_PAGE_BG } from '@/lib/site-layout'
import { cn } from '@/lib/utils'

const bar = 'animate-pulse rounded-md bg-gray-200 dark:bg-slate-700'

/**
 * Ana sayfa loading — kayıtlı / API home masthead layout’una göre.
 */
export default function HomeLoadingSkeleton() {
  const cached = peekHomeMastheadLayout()
  const [layout, setLayout] = useState<MastheadLayoutId>(
    cached ?? MASTHEAD_LAYOUT.CENTER_SIDES,
  )

  useEffect(() => {
    const peek = peekHomeMastheadLayout()
    if (peek) {
      setLayout(peek)
      return
    }

    let cancelled = false
    const ctrl = new AbortController()
    const timer = window.setTimeout(() => ctrl.abort(), 2500)

    void fetch(`${getApiBaseUrl()}/api/settings/home-masthead`, {
      signal: ctrl.signal,
      cache: 'force-cache',
    })
      .then(async (res) => {
        if (!res.ok || cancelled) return
        const data = (await res.json()) as { layout?: unknown }
        const next = parseMastheadLayout(
          String(data.layout ?? ''),
          MASTHEAD_LAYOUT.CENTER_SIDES,
        )
        if (!cancelled) setLayout(next)
      })
      .catch(() => {
        /* varsayılan */
      })
      .finally(() => window.clearTimeout(timer))

    return () => {
      cancelled = true
      ctrl.abort()
      window.clearTimeout(timer)
    }
  }, [])

  return (
    <div className={SITE_PAGE_BG} aria-busy="true" aria-label="Sayfa yükleniyor">
      <main className={`${SITE_CONTAINER} ${SITE_MAIN_PAD_Y}`}>
        <MastheadLayoutSkeleton layout={layout} />
        <div className="mb-6">
          <div className={cn(bar, 'mb-3 h-6 w-28')} />
          <div className="flex gap-2.5 overflow-hidden">
            {Array.from({ length: 6 }, (_, i) => (
              <div
                key={i}
                className={cn(bar, 'h-40 w-[7.25rem] shrink-0 rounded-xl sm:h-44 sm:w-32')}
              />
            ))}
          </div>
        </div>
        <div className={cn(bar, 'mb-4 h-[90px] w-full rounded-[5px]')} aria-hidden />
        <div className="relative mb-4 flex h-[3.25rem] items-center gap-3 rounded-[5px] bg-gray-50 px-3 dark:bg-slate-900">
          <div className={cn(bar, 'h-7 w-7 shrink-0 rounded-full')} />
          <div className={cn(bar, 'h-4 w-20 shrink-0')} />
          <div className="flex min-w-0 flex-1 gap-2 overflow-hidden">
            {Array.from({ length: 5 }, (_, i) => (
              <div key={i} className={cn(bar, 'h-8 min-w-[4.5rem] flex-1 rounded-md')} />
            ))}
          </div>
        </div>
        <div className="mt-6 space-y-3">
          <div className={cn(bar, 'h-7 w-36')} />
          <NewsSkeleton count={8} columns={4} plain showDate={false} />
        </div>
      </main>
    </div>
  )
}
